import type {
  TicketCargoSummary,
  TicketAddressSummary,
  TicketReply,
} from "../models/Ticket";

const pickString = (value: unknown): string => {
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number") {
    return String(value);
  }
  return "";
};

const pickStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((item) => pickString(item))
    .filter(Boolean);
};

const normalizeTicketAddressSummary = (
  value: unknown
): TicketAddressSummary | null => {
  const record =
    typeof value === "object" && value !== null
      ? (value as Record<string, unknown>)
      : null;

  if (!record) {
    return null;
  }

  const nextAddress: TicketAddressSummary = {
    province: pickString(record.province),
    city: pickString(record.city),
    district: pickString(record.district),
    address: pickString(record.address),
  };

  if (
    !nextAddress.province &&
    !nextAddress.city &&
    !nextAddress.district &&
    !nextAddress.address
  ) {
    return null;
  }

  return nextAddress;
};

const pickCargoImage = (record: Record<string, unknown>): string => {
  const candidateImages = [
    record.image,
    record.cover,
    record.coverUrl,
    record.coverImage,
    record.thumbnail,
    record.thumb,
    record.photo,
    record.img,
  ];

  const directImage = candidateImages.map((item) => pickString(item)).find(Boolean);
  if (directImage) {
    return directImage;
  }

  return pickStringArray(record.images ?? record.pictures ?? record.gallery)[0] ?? "";
};

export const isCargoTicketType = (
  rawType?: string | number | null
): boolean => {
  if (rawType === undefined || rawType === null || rawType === "") {
    return false;
  }
  const key = String(rawType);
  return key === "1" || key === "cargo";
};

export const formatTicketCargoAddress = (
  address?: TicketAddressSummary | null
): string => {
  if (!address) {
    return "-";
  }

  const parts = [
    address.province,
    address.city,
    address.district,
    address.address,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join("") : "-";
};

export const hasTicketReplyContent = (
  reply?: Pick<TicketReply, "content" | "images"> | null
): boolean => {
  if (!reply) {
    return false;
  }

  if (reply.content?.trim()) {
    return true;
  }

  return Array.isArray(reply.images) && reply.images.length > 0;
};

export const getTicketReplySummary = (
  reply?: Pick<TicketReply, "content" | "images"> | null
): string => {
  if (!reply) {
    return "暂无沟通内容";
  }

  if (reply.content?.trim()) {
    return reply.content;
  }

  if (Array.isArray(reply.images) && reply.images.length > 0) {
    return "[图片]";
  }

  return "暂无沟通内容";
};

export const normalizeTicketCargoSummary = (
  value: unknown
): TicketCargoSummary | null => {
  const record =
    typeof value === "object" && value !== null
      ? (value as Record<string, unknown>)
      : null;

  if (!record) {
    return null;
  }

  const summary: TicketCargoSummary = {
    id: pickString(record.id ?? record._id ?? record.cargoId),
    name: pickString(record.name ?? record.title ?? record.subject),
    image: pickCargoImage(record),
    from: normalizeTicketAddressSummary(
      record.from ??
        record.origin ??
        record.pickupAddress ??
        record.loadingAddress ??
        record.loadAddress
    ),
    to: normalizeTicketAddressSummary(
      record.to ??
        record.destination ??
        record.receiveAddress ??
        record.unloadingAddress ??
        record.unloadAddress
    ),
  };

  if (
    !summary.id &&
    !summary.name &&
    !summary.image &&
    !summary.from &&
    !summary.to
  ) {
    return null;
  }

  return summary;
};
